/* SamtalFokusTraner — Dedicated ADHD conversation-focus training module.
 * Flow: Choose technique → Active session with timer → Reflection → Stats. */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Target, Play, Check, TrendingUp, Award, Flame, X, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ChooseTechniqueView({ techniques, onStart }) {
  const [selectedId, setSelectedId] = useState(null);
  const [duration, setDuration] = useState(10);
  const [context, setContext] = useState('');
  const [busy, setBusy] = useState(false);

  const start = async () => {
    if (!selectedId) { toast.error('Välj en teknik först 💛'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/samtal-focus/session/start`, {
        technique_id: selectedId,
        planned_min: duration,
        context: context.trim(),
      }, { withCredentials: true });
      onStart(r.data);
    } catch { toast.error('Kunde inte starta'); }
    setBusy(false);
  };

  return (
    <div className="space-y-3" data-testid="samtal-choose-view">
      <p className="text-xs text-gray-700 leading-relaxed">
        Välj EN teknik att öva på idag. Bara en. Fokus på repetition, inte ambition. 🌱
      </p>
      <div className="space-y-2">
        {techniques.map((t) => (
          <button
            key={t.id}
            onClick={() => setSelectedId(t.id)}
            className={`w-full text-left neo-card p-3 transition ${selectedId === t.id ? 'bg-gradient-to-r from-purple-200 to-pink-200 ring-2 ring-purple-500' : 'bg-white hover:bg-purple-50'}`}
            data-testid={`samtal-technique-${t.id}`}
          >
            <div className="font-bold text-sm">{t.title}</div>
            <div className="text-[11px] opacity-70 mt-0.5">{t.short}</div>
            {selectedId === t.id && (
              <div className="text-[11px] mt-2 pt-2 border-t border-purple-300 text-purple-900 italic">
                {t.detail}
              </div>
            )}
          </button>
        ))}
      </div>

      {selectedId && (
        <div className="neo-card p-3 bg-purple-50 space-y-2" data-testid="samtal-setup">
          <label className="block">
            <span className="text-[10px] font-bold uppercase opacity-60">Hur lång tid?</span>
            <div className="flex gap-2 mt-1">
              {[5, 10, 15, 30, 60].map((m) => (
                <button
                  key={m}
                  onClick={() => setDuration(m)}
                  className={`flex-1 py-2 rounded-full font-bold text-sm ${duration === m ? 'bg-purple-500 text-white' : 'bg-white'}`}
                  data-testid={`samtal-duration-${m}`}
                >{m} min</button>
              ))}
            </div>
          </label>
          <label className="block">
            <span className="text-[10px] font-bold uppercase opacity-60">Sammanhang (frivilligt)</span>
            <input
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder="Lunch med barnen, möte med chef..."
              className="neo-input w-full text-sm mt-1"
              data-testid="samtal-context"
            />
          </label>
          <button
            onClick={start}
            disabled={busy}
            className="w-full py-3 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="samtal-start"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            Starta {duration}-min session
          </button>
        </div>
      )}
    </div>
  );
}

function ActiveSessionView({ session, onFinish, onCancel }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const planned = session.planned_min * 60;
  const remaining = Math.max(0, planned - elapsed);
  const mm = Math.floor(remaining / 60);
  const ss = String(remaining % 60).padStart(2, '0');

  return (
    <div className="text-center space-y-4" data-testid="samtal-active-view">
      <div className="text-[10px] font-bold uppercase tracking-widest opacity-60">Session pågår</div>
      <h3 className="text-2xl font-['Fredoka'] font-bold">{session.technique_title}</h3>
      {session.context && <p className="text-xs italic opacity-70">{session.context}</p>}

      <div className="neo-card p-8 bg-gradient-to-br from-[#FFE8F5] via-[#F5EEFF] to-[#E8F0FF]">
        <div className="text-6xl font-bold tabular-nums font-['Fredoka']" data-testid="samtal-timer">
          {mm}:{ss}
        </div>
        <p className="text-[11px] italic opacity-60 mt-2">
          Bär teknikken med dig. Kom tillbaka hit när samtalet är klart 💛
        </p>
      </div>

      <div className="flex gap-2">
        <button
          onClick={onCancel}
          className="flex-1 py-3 rounded-full bg-white border-2 border-black font-bold text-sm"
          data-testid="samtal-cancel"
        >
          <X className="w-4 h-4 inline" /> Avbryt
        </button>
        <button
          onClick={onFinish}
          className="flex-1 py-3 rounded-full bg-gradient-to-r from-emerald-400 to-teal-500 text-white font-bold text-sm"
          data-testid="samtal-finish"
        >
          <Check className="w-4 h-4 inline" /> Samtalet är klart
        </button>
      </div>
    </div>
  );
}

function ReflectionView({ session, onSaved }) {
  const [rating, setRating] = useState(3);
  const [whatWorked, setWhatWorked] = useState('');
  const [whatDrifted, setWhatDrifted] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/samtal-focus/session/${session.id}/finish`, {
        rating,
        what_worked: whatWorked,
        what_drifted: whatDrifted,
      }, { withCredentials: true });
      toast.success('💚 Sparat! Du bygger fokus-muskeln.');
      onSaved();
    } catch { toast.error('Kunde inte spara'); }
    setBusy(false);
  };

  const emojis = ['😵‍💫', '😔', '😐', '🙂', '✨'];
  const labels = ['Drev totalt', 'Drev mycket', 'Halvvägs', 'Bra fokus', 'Fokuserad hela tiden'];

  return (
    <div className="space-y-4" data-testid="samtal-reflect-view">
      <h3 className="text-xl font-['Fredoka'] font-bold">Hur gick det? 🐾</h3>
      <p className="text-xs opacity-70">{session.technique_title}</p>

      <div className="space-y-2">
        <div className="text-[10px] font-bold uppercase opacity-60">Hur mycket kunde du hålla fokus?</div>
        <div className="flex gap-2 justify-between">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              onClick={() => setRating(n)}
              className={`flex-1 py-3 rounded-2xl border-2 transition ${rating === n ? 'bg-purple-500 text-white border-purple-700 scale-105' : 'bg-white border-black/20'}`}
              data-testid={`samtal-rating-${n}`}
            >
              <div className="text-2xl">{emojis[n-1]}</div>
              <div className="text-[9px] font-bold mt-0.5">{labels[n-1]}</div>
            </button>
          ))}
        </div>
      </div>

      <label className="block">
        <span className="text-[10px] font-bold uppercase opacity-60">Vad funkade? (frivilligt)</span>
        <textarea
          value={whatWorked}
          onChange={(e) => setWhatWorked(e.target.value)}
          rows={2}
          placeholder="T.ex. 'Ord-repetitionen höll mig kvar när jag ville drifta'"
          className="neo-input w-full text-sm mt-1 resize-none"
          data-testid="samtal-worked"
        />
      </label>

      <label className="block">
        <span className="text-[10px] font-bold uppercase opacity-60">Vad drev iväg? (frivilligt)</span>
        <textarea
          value={whatDrifted}
          onChange={(e) => setWhatDrifted(e.target.value)}
          rows={2}
          placeholder="T.ex. 'Tappade tråden när de bytte ämne'"
          className="neo-input w-full text-sm mt-1 resize-none"
          data-testid="samtal-drifted"
        />
      </label>

      <button
        onClick={submit}
        disabled={busy}
        className="w-full py-3 rounded-full bg-gradient-to-r from-emerald-400 to-teal-500 text-white font-bold flex items-center justify-center gap-2"
        data-testid="samtal-save"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
        Spara reflektion
      </button>
    </div>
  );
}

function StatsView() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/samtal-focus/stats`, { withCredentials: true });
        setStats(r.data);
      } catch { /* silent */ }
    })();
  }, []);

  if (!stats) return <div className="text-center py-6 opacity-50 text-xs">Laddar statistik...</div>;

  if (stats.total_sessions_30d === 0) {
    return (
      <div className="text-center py-8 space-y-2" data-testid="samtal-empty-stats">
        <div className="text-4xl">🌱</div>
        <p className="text-sm opacity-70 italic">
          Inga sessioner än. Kör en första — sen kommer statistiken hit.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="samtal-stats-view">
      <div className="grid grid-cols-3 gap-2">
        <div className="neo-card p-3 bg-purple-50 text-center">
          <div className="text-2xl font-bold" data-testid="samtal-stat-sessions">{stats.total_sessions_30d}</div>
          <div className="text-[10px] opacity-60">sessioner (30d)</div>
        </div>
        <div className="neo-card p-3 bg-emerald-50 text-center">
          <div className="text-2xl font-bold flex items-center justify-center gap-1">
            <Flame className="w-5 h-5 text-orange-500" />
            <span data-testid="samtal-stat-streak">{stats.streak_days}</span>
          </div>
          <div className="text-[10px] opacity-60">dagar i rad</div>
        </div>
        <div className="neo-card p-3 bg-amber-50 text-center">
          <div className="text-2xl font-bold" data-testid="samtal-stat-avg">{stats.avg_rating_30d}</div>
          <div className="text-[10px] opacity-60">snittfokus</div>
        </div>
      </div>

      {stats.best_technique && (
        <div className="neo-card p-3 bg-gradient-to-r from-yellow-100 to-amber-100 border-2 border-amber-300" data-testid="samtal-best">
          <div className="flex items-center gap-2">
            <Award className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <div>
              <div className="text-[10px] font-bold uppercase opacity-60">Din bästa teknik</div>
              <div className="text-sm font-bold">{stats.best_technique.title}</div>
              <div className="text-[11px] opacity-70">
                {stats.best_technique.count} sessioner · snitt {stats.best_technique.avg_rating}/5
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-1.5">
        <div className="text-[10px] font-bold uppercase tracking-widest opacity-60">Ranking</div>
        {stats.per_technique.map((t, i) => (
          <div key={t.technique_id} className="neo-card p-2 bg-white flex items-center gap-2" data-testid={`samtal-rank-${t.technique_id}`}>
            <div className="text-lg opacity-40 w-6 text-center font-bold">{i + 1}</div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold truncate">{t.title}</div>
              <div className="text-[10px] opacity-60">{t.count} sessioner</div>
            </div>
            <div className="text-sm font-bold text-purple-700">{t.avg_rating}/5</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SamtalFokusTraner() {
  const [phase, setPhase] = useState('choose'); // choose | active | reflect | stats
  const [techniques, setTechniques] = useState([]);
  const [session, setSession] = useState(null);

  const loadTechniques = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/samtal-focus/techniques`, { withCredentials: true });
      setTechniques(r.data.items || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadTechniques(); }, [loadTechniques]);

  return (
    <div className="space-y-4" data-testid="samtal-fokus-traner">
      <header className="neo-card p-4 bg-gradient-to-br from-[#FFF7E6] via-[#FFE8F5] to-[#E8E0FF]">
        <div className="flex items-center gap-2">
          <Target className="w-6 h-6" />
          <div>
            <h2 className="text-lg font-bold font-['Fredoka']">Samtals-fokus-tränare</h2>
            <p className="text-[11px] opacity-70 italic">Träna EN teknik åt gången. Bygg fokus-muskeln 🎯</p>
          </div>
        </div>
      </header>

      {phase === 'choose' && (
        <>
          <div className="flex gap-2">
            <button onClick={() => setPhase('choose')} className="neo-btn flex-1 py-2 bg-purple-200 font-bold text-xs" data-testid="samtal-tab-choose">
              🎯 Ny session
            </button>
            <button onClick={() => setPhase('stats')} className="neo-btn flex-1 py-2 bg-white font-bold text-xs" data-testid="samtal-tab-stats">
              <TrendingUp className="w-3 h-3 inline mr-1" /> Statistik
            </button>
          </div>
          <ChooseTechniqueView techniques={techniques} onStart={(s) => { setSession(s); setPhase('active'); }} />
        </>
      )}

      {phase === 'active' && session && (
        <ActiveSessionView
          session={session}
          onFinish={() => setPhase('reflect')}
          onCancel={() => { setSession(null); setPhase('choose'); }}
        />
      )}

      {phase === 'reflect' && session && (
        <ReflectionView session={session} onSaved={() => { setSession(null); setPhase('stats'); }} />
      )}

      {phase === 'stats' && (
        <>
          <div className="flex gap-2">
            <button onClick={() => setPhase('choose')} className="neo-btn flex-1 py-2 bg-white font-bold text-xs" data-testid="samtal-tab-choose">
              🎯 Ny session
            </button>
            <button onClick={() => setPhase('stats')} className="neo-btn flex-1 py-2 bg-purple-200 font-bold text-xs" data-testid="samtal-tab-stats">
              <TrendingUp className="w-3 h-3 inline mr-1" /> Statistik
            </button>
          </div>
          <StatsView />
        </>
      )}
    </div>
  );
}
